import * as React from 'react';
import { store } from 'Models/Store';
import { appConfigStore } from 'Models/AppConfigStore';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { PassengerTypeKey } from 'Models/Enums';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { modalWithCustomInput } from 'Views/Components/Modal/CustomModal';
import { stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import If from 'Views/Components/If/If';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { PassengerDetail } from '../../../../BookingWizardData/PassengerDetail';

export interface PassengerRemovalModalProps {
	model: {
		passengerToRemoveId: string;
		error: string;
	};
	passengers: PassengerDetail[];
}

/**
 * Whether removing a passenger always asks which one.
 *
 * <p>With fare classes on, passengers of one type are no longer interchangeable: two adults can be in
 * different classes and hold different seats, so dropping "the last one" silently decides which fare
 * and which seat the booking gives up. Without classes they are, so the counter keeps quietly taking
 * an empty one off the end.</p>
 */
export const passengerRemovalAlwaysAsks = () => appConfigStore.passengerClassesEnabled;

/**
 * The passenger's name, else their type and position — an unnamed traveller still needs a label.
 *
 * <p>Only where the site collects names. With them off, a passenger can still carry one filled in from
 * the booking's customer, and showing it labelled one traveller by the booker's name while the rest
 * read `Adult 2`, `Adult 3`.</p>
 */
const passengerLabel = (passenger: PassengerDetail, passengers: PassengerDetail[]) => {
	const namesShown = appConfigStore.passengerInfoEnabled;
	const firstName = namesShown && appConfigStore.passengerFirstNameEnabled ? passenger.firstName ?? '' : '';
	const lastName = namesShown && appConfigStore.passengerLastNameEnabled ? passenger.lastName ?? '' : '';
	const name = `${firstName} ${lastName}`.trim();
	const driver = passenger.driver ? ' (driver)' : '';
	if (stringNotEmpty(name)) {
		return `${name}${driver}`;
	}

	const sameType = passengers.filter(x => x.key === passenger.key);
	const typeName = passengerTypeStore.getSingularName(passenger.key as PassengerTypeKey, true);
	const position = sameType.length > 1 ? ` ${sameType.indexOf(passenger) + 1}` : '';
	return `${typeName}${position}${driver}`;
};

/**
 * One value per leg, labelled only where the legs differ. A return booking holds a class and a seat
 * on each sailing, and showing only the outbound one would hide half of what is being given up.
 */
const perLeg = (
	departing: string | undefined,
	returning: string | undefined,
	hasReturn: boolean,
): string | undefined => {
	if (!hasReturn || departing === returning) {
		return stringNotEmpty(departing) ? departing : undefined;
	}
	if (stringIsEmpty(departing) && stringIsEmpty(returning)) {
		return undefined;
	}
	return `${departing || '—'} (departing) · ${returning || '—'} (returning)`;
};

/**
 * Whether this passenger travels a returning leg. Read from the returning fare type, which every
 * return passenger is given, because a missing returning seat is exactly the case being asked about:
 * a seat held outbound only must say so, not read as though it covered both sailings.
 */
const travelsReturn = (passenger: PassengerDetail) =>
	stringNotEmpty(passenger.returningPassengerClassId) || stringNotEmpty(passenger.returningSeatLabel);

const passengerClass = (passenger: PassengerDetail) =>
	perLeg(passenger.passengerClassName, passenger.returningPassengerClassName, travelsReturn(passenger));

const passengerSeat = (passenger: PassengerDetail) =>
	perLeg(passenger.seatLabel, passenger.returningSeatLabel, travelsReturn(passenger));

const PassengerRemovalDetails = ({ passenger }: { passenger: PassengerDetail }) => {
	const seat = passengerSeat(passenger);

	return (
		<dl className="passenger-removal-modal-content__details">
			<div>
				<dt>Passenger type</dt>
				<dd>{passengerTypeStore.getSingularName(passenger.key as PassengerTypeKey, true)}</dd>
			</div>
			<div>
				<dt>Fare type</dt>
				<dd>{passengerClass(passenger) ?? '—'}</dd>
			</div>
			<If condition={!!seat}>
				<div>
					<dt>Seat</dt>
					<dd>{seat}</dd>
				</div>
			</If>
		</dl>
	);
};

interface PassengerRemovalOption {
	label: string;
	/** Fare type and seat, shown under the name in the open list. Empty with fare types off. */
	detail: string;
	value: string;
	isDisabled: boolean;
}

export const PassengerRemovalModalContent = observer(({ model, passengers }: PassengerRemovalModalProps) => {
	const disableDriver = passengers.length === 1 || !passengers.some(x => !x.driver);
	const showClasses = appConfigStore.passengerClassesEnabled;
	const selected = passengers.find(x => x.id === model.passengerToRemoveId);
	const options: PassengerRemovalOption[] = passengers.map(x => ({
		label: passengerLabel(x, passengers),
		detail: showClasses ? [passengerClass(x), passengerSeat(x)].filter(stringNotEmpty).join(' · ') : '',
		value: x.id,
		isDisabled: !disableDriver && x.driver,
	}));

	return (
		<div className="passenger-removal-modal-content">
			<p>Choose the passenger you would like to remove from the booking.</p>
			<SelectInput
				label="Passenger to remove"
				placeholder="Search for a passenger"
				required
				error={stringNotEmpty(model.error) ? model.error : undefined}
				options={options}
				value={options.find(x => x.value === model.passengerToRemoveId) ?? null}
				onChange={(option: any) =>
					runInAction(() => {
						model.passengerToRemoveId = option?.value ?? '';
					})
				}
				renderOption={(option: PassengerRemovalOption) => (
					// Tailwind rather than the modal's stylesheet: the menu is portalled to <body>, outside the
					// `.frontend` wrapper every SCSS rule is compiled under, so class-based SCSS never reaches it.
					<div className="flex flex-col gap-0.5">
						<span>{option.label}</span>
						<If condition={stringNotEmpty(option.detail)}>
							<span className="text-xs text-gray-500">{option.detail}</span>
						</If>
					</div>
				)}
				data-test-id="passenger-removal-select"
			/>
			<If condition={showClasses && !!selected}>
				{selected && <PassengerRemovalDetails passenger={selected} />}
			</If>
		</div>
	);
});

export const RenderPassengerRemovalModal = async (passengers: PassengerDetail[]): Promise<string | null> => {
	const { isManager } = store;

	const model = observable({
		// With one passenger there is nothing to choose, so the modal opens on them and only confirms.
		passengerToRemoveId: passengers.length === 1 ? passengers[0].id : '',
		error: '',
	});

	const confirmed = await modalWithCustomInput(
		'Remove passenger',
		<PassengerRemovalModalContent model={model} passengers={passengers} />,
		{
			cancelText: 'Back',
			confirmText: 'Confirm',
			className: 'passenger-removal-modal',
			actionClassName: 'passenger-removal-modal',
			resolveOnCancel: true,
			onConfirm: action(() => {
				if (stringNotEmpty(model.passengerToRemoveId)) {
					model.error = '';
					return true;
				}

				model.error = 'Please select a passenger.';
				return false;
			}),
		},
	);

	if (!confirmed) {
		return null;
	}

	return model.passengerToRemoveId;
};
